import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useLawRequests from "../../hooks/lawyer/useLawRequests";
import useTranslate from "../../hooks/useTranslate";
import LawRequestCard from "./LawRequestCard";

const reqId = (r) => r?.id ?? r?.request_id;

// D-010: "Law Requests" grouping shown ABOVE the chat list for lawyers only.
// It is a self-contained subtree — it does NOT touch the GetStream ChannelList.
// Accepting opens the freshly created channel via the inbox's location.state
// channel-opening path (same as organizer/club chats).
const LawRequestsSection = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { isLawyer, requests, count, loading, accept, decline } = useLawRequests();

  // Non-lawyers see nothing here → their inbox is unchanged.
  if (!isLawyer) return null;

  const handleAccept = async (id) => {
    const res = await accept(id);
    if (res.success && res.channelId) {
      toast.success(t("Request accepted"));
      navigate("/inbox", {
        state: { channelId: res.channelId, channelType: res.channelType },
      });
    } else if (res.success) {
      toast.success(t("Request accepted"));
    } else {
      toast.error(res.error || t("Failed to accept request"));
    }
  };

  const handleDecline = async (id) => {
    const res = await decline(id);
    if (res.success) toast.success(t("Request declined"));
    else toast.error(res.error || t("Failed to decline request"));
  };

  return (
    <div className="flex-none px-4 pb-3 border-b border-[#E6EAF2] mb-2">
      <div className="flex items-center gap-2 py-2.5">
        <p className="text-[10px] font-semibold text-[#111927] uppercase tracking-wider">
          {t("Law Requests")}
        </p>
        {count > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#2283FF] text-white text-[11px]">
            {count}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-3 flex justify-center">
          <div className="h-4 w-4 rounded-full border-2 border-[#DDE3EC] border-t-[#2283FF] animate-spin" />
        </div>
      ) : count === 0 ? (
        <p className="text-[12px] text-[#98A2B3] pb-1">
          {t("No pending requests")}
        </p>
      ) : (
        <div className="space-y-2 max-h-[42vh] overflow-y-auto pr-0.5">
          {requests.map((r) => (
            <LawRequestCard
              key={reqId(r)}
              request={r}
              onAccept={handleAccept}
              onDecline={handleDecline}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LawRequestsSection;
